import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Star, Share2, Sparkles, Layers, TrendingUp, TrendingDown, Minus, DollarSign, Clock } from 'lucide-react';
import { Card, CardTitle, Skeleton, Button, Stat } from '@/components/ui';
import {
  GodScoreGauge,
  SaturationBar,
  HoneyScoreBar,
  OpportunityStars,
  KeywordTable,
  TrendChart,
  SearchBar,
  ScoreBadge,
  type KeywordRow,
} from '@/components/keyword';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import {
  formatNumber,
  matchCategoriesForKeyword,
  type ChannelDistribution,
  type KeywordAnalysisResponse,
} from '@keywordgod/shared';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

export function KeywordAnalysis() {
  const { keyword: raw } = useParams<{ keyword: string }>();
  const keyword = raw ? decodeURIComponent(raw) : '';

  const q = useQuery({
    queryKey: ['analyze', keyword],
    queryFn: () => api.keyword.analyze(keyword),
    enabled: !!keyword,
    staleTime: 60_000,
  });

  return (
    <div className="space-y-8">
      <SearchBar initial={keyword} autoFocus={!keyword} />

      {!keyword && (
        <Card>
          <CardTitle>분석할 키워드를 입력하세요</CardTitle>
          <p className="mt-1 text-sm text-gray-400">
            상단 검색창에 키워드를 입력하면 GodScore · 포화도 · 꿀통 · 기회점수를 즉시 보여드립니다.
          </p>
        </Card>
      )}

      {q.isLoading && <AnalysisSkeleton />}
      {q.isError && (
        <Card className="border-red-500/40 bg-red-500/10 text-red-300">
          <div className="font-bold">분석에 실패했습니다</div>
          <div className="mt-1 text-sm">{(q.error as Error).message}</div>
        </Card>
      )}

      {q.data && <AnalysisView data={q.data} />}
    </div>
  );
}

// ─── 본문 ────────────────────────────────────────────────────

function AnalysisView({ data }: { data: KeywordAnalysisResponse }) {
  const m = data.metrics;
  return (
    <>
      <HeroBlock data={data} />

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <SaturationBar value={m.saturation.value} level={m.saturation.level} />
        </Card>
        <Card>
          <HoneyScoreBar value={m.honeyScore.value} max={m.honeyScore.max} />
        </Card>
        <Card>
          <OpportunityStars value={m.opportunity.value} max={m.opportunity.max} />
        </Card>
      </section>

      <Card>
        <CardTitle>상세 메트릭스</CardTitle>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5">
          <Stat label="총 검색량" value={formatNumber(m.totalSearch)} />
          <Stat label="PC 검색" value={formatNumber(m.monthlyPc)} />
          <Stat label="Mobile 검색" value={formatNumber(m.monthlyMobile)} />
          <Stat label="블로그 문서" value={formatNumber(m.blogTotal)} />
          <Stat
            label="경쟁비율"
            value={
              m.totalSearch > 0
                ? `${((m.blogTotal / m.totalSearch) * 100).toFixed(2)}%`
                : '-'
            }
          />
          <Stat
            label="경쟁도"
            value={<CompetitionBadge level={m.compIdx} />}
          />
        </div>
      </Card>

      <Card>
        <div className="flex items-center justify-between">
          <CardTitle>최근 30일 트렌드</CardTitle>
          <span className="text-xs text-gray-500">{data.freshness}</span>
        </div>
        <div className="mt-4">
          <TrendChart data={data.trend} />
        </div>
      </Card>

      {data.timing && data.timing.type !== 'unknown' && <TimingCard timing={data.timing} />}

      <ChannelDistributionCard keyword={data.keyword} />

      <BestTimeCard keyword={data.keyword} />

      {data.monetization && <MonetizationCard mon={data.monetization} />}


      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            <CardTitle>연관 키워드</CardTitle>
            <p className="mt-1 text-sm text-gray-400">
              상위 {data.relatedKeywords.length}개 · 행 클릭 시 해당 키워드 분석
            </p>
            <div className="mt-4">
              <KeywordTable rows={toRows(data.relatedKeywords)} />
            </div>
          </Card>
        </div>

        <div>
          <Card className="border-gold/40 bg-gradient-to-br from-gold/5 to-transparent">
            <CardTitle className="flex items-center gap-2 text-gold-gradient">
              <Sparkles className="h-5 w-5 text-gold" /> 이 키워드 대신 이걸 써보세요!
            </CardTitle>
            <p className="mt-1 text-sm text-gray-400">GodScore가 더 높은 대안 5개</p>
            <ul className="mt-4 space-y-2">
              {data.alternatives.length === 0 && (
                <li className="text-sm text-gray-500">
                  이 키워드보다 점수 높은 대안이 없어요. 이미 우수합니다!
                </li>
              )}
              {data.alternatives.map((a) => (
                <li key={a.keyword}>
                  <a
                    href={`/analyze/${encodeURIComponent(a.keyword)}`}
                    className="flex items-center justify-between rounded-lg border border-gray-800 bg-gray-950 px-3 py-2.5 hover:border-gold/60"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium text-white">{a.keyword}</div>
                      <div className="text-xs text-gray-500">
                        월 {formatNumber(a.totalSearch)} · {a.competition}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="tabular text-sm text-gold">{a.godScore}</span>
                      <ScoreBadge grade={a.grade} size="sm" />
                    </div>
                  </a>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </section>

      <FloatingActions keyword={data.keyword} />
    </>
  );
}

// ─── Hero 블록 (큰 게이지 + 등급 + verdict) ──────────────

function HeroBlock({ data }: { data: KeywordAnalysisResponse }) {
  const varied =
    !!data.originalKeyword && data.originalKeyword.trim() !== data.keyword.trim();
  // Phase 28: 자동 카테고리 매칭 (substring contains)
  const categories = matchCategoriesForKeyword(data.keyword).slice(0, 3);
  return (
    <Card className="overflow-hidden">
      {varied && (
        <div className="-mt-2 -mx-2 mb-4 rounded-lg border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-xs text-amber-200">
          <span className="font-bold">자동 변형:</span> 입력하신{' '}
          <code className="rounded bg-amber-400/20 px-1.5 py-0.5 font-semibold">
            {data.originalKeyword}
          </code>{' '}
          은(는) 네이버 광고 데이터에 없어{' '}
          <code className="rounded bg-amber-400/20 px-1.5 py-0.5 font-semibold">
            {data.keyword}
          </code>{' '}
          으로 분석했습니다.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 md:gap-10 items-center">
        <div className="flex justify-center md:justify-start">
          <GodScoreGauge score={data.godScore} grade={data.grade} size={220} />
        </div>
        <div className="text-center md:text-left">
          <div className="flex flex-wrap items-center gap-3 justify-center md:justify-start">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
              {data.keyword}
            </h1>
            <ScoreBadge grade={data.grade} size="lg" />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-1.5 justify-center md:justify-start">
            {categories.map((c) => (
              <Link
                key={c.id}
                to="/categories"
                className="inline-flex items-center gap-1 rounded-full border border-gold/30 bg-gold/5 px-2 py-0.5 text-[11px] font-semibold text-gold hover:bg-gold/15"
                title={c.description}
              >
                <span>{c.emoji}</span>
                <span>{c.label}</span>
              </Link>
            ))}
            {/* Phase 32: 트렌드 배지 */}
            {typeof data.trendRate === 'number' && data.trendInfo && data.trendInfo.points > 0 && (
              <TrendBadge rate={data.trendRate} />
            )}
          </div>
          <p className="mt-4 text-xl md:text-2xl font-bold text-gold-gradient">
            "{data.verdict}"
          </p>
          <p className="mt-3 text-xs text-gray-500">
            데이터 신선도: {data.freshness} · 캐시 {new Date(data.cachedAt * 1000).toLocaleString('ko-KR')}
          </p>
        </div>
      </div>
    </Card>
  );
}

// ─── 경쟁도 배지 ────────────────────────────────────────

function CompetitionBadge({ level }: { level: '낮음' | '중간' | '높음' }) {
  const color =
    level === '낮음' ? 'bg-emerald-600' : level === '중간' ? 'bg-blue-600' : 'bg-red-600';
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-bold text-white ${color}`}>
      {level}
    </span>
  );
}

// ─── 트렌드 배지 (Phase 32) ──────────────────────────────

function TrendBadge({ rate }: { rate: number }) {
  // 5% 미만 변화는 "정체" 로 간주
  const pct = Math.round(rate * 100);
  const isUp = rate >= 0.05;
  const isDown = rate <= -0.05;
  const Icon = isUp ? TrendingUp : isDown ? TrendingDown : Minus;
  const color = isUp
    ? 'border-emerald-400/40 bg-emerald-400/10 text-emerald-300'
    : isDown
    ? 'border-red-400/40 bg-red-400/10 text-red-300'
    : 'border-gray-700 bg-gray-800 text-gray-300';
  const label = isUp
    ? `최근 트렌드 +${pct}%`
    : isDown
    ? `최근 트렌드 ${pct}%`
    : '최근 트렌드 정체';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${color}`}
      title="네이버 데이터랩 — 최근 30일 평균 vs 이전 30일 평균 변화율"
    >
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}

// ─── 타이밍 진단 카드 (Phase 59) — 폭발형 vs 지속형 ──────

function TimingCard({ timing }: { timing: NonNullable<KeywordAnalysisResponse['timing']> }) {
  const toneByType: Record<string, string> = {
    golden: 'border-gold/50 bg-gradient-to-br from-gold/10 to-transparent',
    burst: 'border-orange-400/50 bg-gradient-to-br from-orange-400/10 to-transparent',
    evergreen: 'border-emerald-400/50 bg-gradient-to-br from-emerald-400/10 to-transparent',
    rising: 'border-blue-400/40 bg-blue-400/5',
    stable: 'border-gray-700 bg-gray-900/40',
    declining: 'border-red-500/40 bg-red-500/5',
  };
  const tone = toneByType[timing.type] ?? 'border-gray-800';
  return (
    <Card className={cn('border', tone)}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="text-4xl leading-none">{timing.emoji}</span>
          <div>
            <CardTitle>타이밍 진단</CardTitle>
            <p className="mt-1 text-sm font-semibold text-white">{timing.label}</p>
          </div>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TimingGauge
          label="🚀 폭발력"
          sub="지금 쓰면 터지나"
          score={timing.burstScore}
          activeColor="bg-orange-400"
          textColor="text-orange-300"
        />
        <TimingGauge
          label="💎 지속력"
          sub="묻어두면 꾸준한가"
          score={timing.evergreenScore}
          activeColor="bg-emerald-400"
          textColor="text-emerald-300"
        />
      </div>

      <div className="mt-4 flex items-center gap-4 text-xs text-gray-500">
        <span>
          60일 추세{' '}
          <strong className={timing.trendRate > 0.05 ? 'text-emerald-300' : timing.trendRate < -0.05 ? 'text-red-300' : 'text-gray-400'}>
            {timing.trendRate > 0 ? '+' : ''}
            {Math.round(timing.trendRate * 100)}%
          </strong>
        </span>
        <span>
          변동성{' '}
          <strong className="text-gray-300">{timing.volatility < 0.3 ? '낮음(안정)' : timing.volatility < 0.6 ? '보통' : '높음(불안정)'}</strong>
        </span>
      </div>
    </Card>
  );
}

function TimingGauge({
  label,
  sub,
  score,
  activeColor,
  textColor,
}: {
  label: string;
  sub: string;
  score: number;
  activeColor: string;
  textColor: string;
}) {
  return (
    <div className="rounded-xl border border-gray-800 bg-gray-950/60 px-4 py-3">
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-bold text-white">{label}</span>
        <span className={cn('tabular text-2xl font-extrabold', textColor)}>{score}</span>
      </div>
      <div className="text-[11px] text-gray-500">{sub}</div>
      <div className="mt-2 h-2.5 w-full rounded-full bg-gray-900 overflow-hidden">
        <div
          className={cn('h-full transition-[width] duration-500', activeColor)}
          style={{ width: `${score}%` }}
        />
      </div>
    </div>
  );
}

// ─── 수익화 잠재력 카드 (Phase 40) ──────────────────────

function MonetizationCard({
  mon,
}: {
  mon: NonNullable<KeywordAnalysisResponse['monetization']>;
}) {
  const fmtKrw = (n: number) =>
    n.toLocaleString('ko-KR', { maximumFractionDigits: 0 }) + '원';
  return (
    <Card className="border-emerald-400/30 bg-gradient-to-br from-emerald-400/5 to-transparent">
      <CardTitle className="flex items-center gap-2">
        <DollarSign className="h-5 w-5 text-emerald-300" /> 월 예상 애드포스트 수익
      </CardTitle>
      <p className="mt-1 text-sm text-gray-400">
        월 클릭 {formatNumber(mon.monthlyClicks)} · 추정 CPC{' '}
        <span className="font-bold text-emerald-300">{fmtKrw(mon.estimatedCpc)}</span> (경쟁도 기반)
      </p>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <ScenarioPill
          rank="1page 상위"
          share="~30%"
          amount={mon.scenarios.top1page}
          tone="emerald"
          fmt={fmtKrw}
        />
        <ScenarioPill
          rank="1page 평범"
          share="~10%"
          amount={mon.scenarios.mid1page}
          tone="gold"
          fmt={fmtKrw}
        />
        <ScenarioPill
          rank="2~3page"
          share="~3%"
          amount={mon.scenarios.lowPage}
          tone="gray"
          fmt={fmtKrw}
        />
      </div>
      <p className="mt-3 text-[11px] text-gray-500">
        ※ 추정치 — 실제 수익은 광고 노출 단가·계절성·블로그 성격에 따라 변동.
      </p>
    </Card>
  );
}

function ScenarioPill({
  rank,
  share,
  amount,
  tone,
  fmt,
}: {
  rank: string;
  share: string;
  amount: number;
  tone: 'emerald' | 'gold' | 'gray';
  fmt: (n: number) => string;
}) {
  const colors = {
    emerald: 'border-emerald-400/50 bg-emerald-400/10 text-emerald-200',
    gold: 'border-gold/40 bg-gold/10 text-gold',
    gray: 'border-gray-700 bg-gray-900 text-gray-300',
  }[tone];
  return (
    <div className={`rounded-xl border px-4 py-3 ${colors}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{rank}</div>
      <div className="mt-1 tabular text-2xl font-extrabold">{fmt(amount)}</div>
      <div className="mt-1 text-[11px] opacity-70">클릭 점유 {share}</div>
    </div>
  );
}

// ─── 영역별 분포 (Phase 24) ──────────────────────────────

const CHANNEL_META: Array<{
  key: keyof Omit<ChannelDistribution, 'keyword' | 'fetchedAt' | 'total'>;
  label: string;
  hint: string;
  /** 색상 — Tailwind 클래스. 막대 + 텍스트 */
  barClass: string;
  textClass: string;
}> = [
  { key: 'blog', label: '블로그', hint: '블로그 포스트', barClass: 'bg-emerald-500', textClass: 'text-emerald-300' },
  { key: 'cafe', label: '카페', hint: '카페글', barClass: 'bg-blue-500', textClass: 'text-blue-300' },
  { key: 'news', label: '뉴스', hint: '뉴스 기사', barClass: 'bg-amber-500', textClass: 'text-amber-300' },
  { key: 'kin', label: '지식인', hint: '네이버 지식iN', barClass: 'bg-purple-500', textClass: 'text-purple-300' },
  { key: 'shop', label: '쇼핑', hint: '쇼핑 상품', barClass: 'bg-pink-500', textClass: 'text-pink-300' },
  { key: 'web', label: '웹', hint: '웹문서', barClass: 'bg-gray-500', textClass: 'text-gray-300' },
];

function ChannelDistributionCard({ keyword }: { keyword: string }) {
  const q = useQuery({
    queryKey: ['channels', keyword],
    queryFn: () => api.keyword.channels(keyword),
    enabled: !!keyword,
    // 서버에서 6h 캐시 → 클라이언트는 길게 잡아도 무방
    staleTime: 60 * 60 * 1000,
  });

  const max = useMemo(() => {
    if (!q.data) return 0;
    return Math.max(q.data.blog, q.data.cafe, q.data.web, q.data.news, q.data.kin, q.data.shop);
  }, [q.data]);

  return (
    <Card>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <CardTitle className="flex items-center gap-2">
          <Layers className="h-5 w-5 text-gold" /> 영역별 노출 분포
        </CardTitle>
        {q.data && (
          <span className="text-xs text-gray-500">
            총 {formatNumber(q.data.total)}건 · 6채널 합산
          </span>
        )}
      </div>
      <p className="mt-1 text-sm text-gray-400">
        네이버 검색 결과의 영역별 문서 수입니다. 막대가 길수록 해당 영역에 콘텐츠가 많음 (=
        포화·경쟁이 높음).
      </p>

      <div className="mt-5">
        {q.isLoading && (
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-7 w-full" />
            ))}
          </div>
        )}
        {q.isError && (
          <p className="text-sm text-red-400">
            영역별 분포를 가져오지 못했습니다 — {(q.error as Error).message}
          </p>
        )}
        {q.data && (
          <ul className="space-y-2.5">
            {CHANNEL_META.map((m) => {
              const v = q.data![m.key] ?? 0;
              const ratio = max > 0 ? v / max : 0;
              const pct = (ratio * 100).toFixed(1);
              return (
                <li key={m.key} className="flex items-center gap-3">
                  <div className="w-16 shrink-0 text-sm font-semibold text-gray-300" title={m.hint}>
                    {m.label}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="h-6 w-full rounded-md bg-gray-900 overflow-hidden">
                      <div
                        className={`h-full ${m.barClass} transition-[width] duration-500`}
                        style={{ width: `${Math.max(ratio * 100, 1.5)}%` }}
                      />
                    </div>
                  </div>
                  <div
                    className={`w-28 shrink-0 text-right tabular text-sm font-bold ${m.textClass}`}
                  >
                    {formatNumber(v)}
                  </div>
                  <div className="w-12 shrink-0 text-right tabular text-xs text-gray-500">
                    {pct}%
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Card>
  );
}

// ─── 발행 최적 요일 (Phase 42) ───────────────────────────

function BestTimeCard({ keyword }: { keyword: string }) {
  const q = useQuery({
    queryKey: ['best-time', keyword],
    queryFn: () => api.keyword.bestTime(keyword),
    enabled: !!keyword,
    staleTime: 60 * 60 * 1000,
    retry: false,
  });

  // 에러 시 카드 자체 미노출 (DataLab 데이터 없음 등)
  if (q.isError) return null;

  return (
    <Card className="border-violet-400/30 bg-violet-400/5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-violet-300" /> 발행 최적 요일
        </CardTitle>
        {q.data && (
          <span className="text-xs text-gray-500">
            60일 DataLab · 신뢰도 {q.data.recommendation.significance === 'high' ? '높음' : q.data.recommendation.significance === 'medium' ? '보통' : '낮음'}
          </span>
        )}
      </div>
      <p className="mt-1 text-sm text-gray-400">
        검색이 가장 몰리는 요일을 찾아 그 직전에 발행하면 신선도 + 검색 피크 둘 다 잡습니다.
      </p>

      {q.isLoading && (
        <div className="mt-4 space-y-2">
          {Array.from({ length: 7 }).map((_, i) => (
            <Skeleton key={i} className="h-6 w-full" />
          ))}
        </div>
      )}

      {q.data && (
        <>
          <div className="mt-4 rounded-lg border border-violet-400/40 bg-violet-400/10 p-3">
            <div className="text-xs text-violet-200/70">추천 발행</div>
            <div className="mt-0.5 text-lg font-bold text-white">
              {q.data.recommendation.publishLabel}요일 {q.data.recommendation.publishHourHint}
            </div>
            <div className="mt-1 text-xs text-violet-100/80">
              {q.data.recommendation.reason}
            </div>
          </div>

          <div className="mt-4 space-y-1.5">
            {q.data.dayStats.map((d) => {
              const isPeak = d.dow === q.data!.peakDay.dow;
              const max = q.data!.peakDay.avgRatio || 1;
              const pct = (d.avgRatio / max) * 100;
              return (
                <div key={d.dow} className="flex items-center gap-3">
                  <div className={`w-8 shrink-0 text-sm font-bold ${isPeak ? 'text-violet-300' : 'text-gray-400'}`}>
                    {d.label}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="h-5 w-full rounded-md bg-gray-900 overflow-hidden">
                      <div
                        className={`h-full transition-[width] duration-500 ${isPeak ? 'bg-violet-400' : 'bg-violet-500/40'}`}
                        style={{ width: `${Math.max(pct, 1.5)}%` }}
                      />
                    </div>
                  </div>
                  <div className={`w-16 shrink-0 text-right tabular text-xs ${isPeak ? 'text-violet-300 font-bold' : 'text-gray-500'}`}>
                    {d.avgRatio.toFixed(1)}
                    {isPeak && ' 👑'}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </Card>
  );
}

// ─── 변환 ────────────────────────────────────────────────

function toRows(arr: KeywordAnalysisResponse['relatedKeywords']): KeywordRow[] {
  return arr.map((a) => ({
    keyword: a.keyword,
    totalSearch: a.totalSearch,
    godScore: a.godScore,
    grade: a.grade,
  }));
}

// ─── 플로팅 액션 ─────────────────────────────────────────

function FloatingActions({ keyword }: { keyword: string }) {
  const user = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const favorites = useQuery({
    queryKey: ['me', 'favorites'],
    queryFn: () => api.me.favorites(),
    enabled: !!user,
  });
  const isFav = useMemo(
    () => !!favorites.data?.some((f) => f.keyword === keyword),
    [favorites.data, keyword],
  );

  const toggle = useMutation({
    mutationFn: async () => {
      if (isFav) return api.me.removeFavorite(keyword);
      return api.me.addFavorite(keyword);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'favorites'] }),
  });

  const [copied, setCopied] = useState(false);

  return (
    <div className="fixed bottom-6 right-6 z-30 flex flex-col gap-2">
      <button
        type="button"
        onClick={() => {
          if (!user) {
            navigate('/');
            return;
          }
          toggle.mutate();
        }}
        className={`group inline-flex h-12 w-12 items-center justify-center rounded-full shadow-lg transition-colors ${
          isFav ? 'bg-gold text-black' : 'bg-gray-900 text-gold border border-gold/50 hover:bg-gray-800'
        }`}
        aria-label={isFav ? '즐겨찾기 해제' : '즐겨찾기 추가'}
        title={isFav ? '즐겨찾기 해제' : '즐겨찾기 추가'}
      >
        <Star className="h-5 w-5" fill={isFav ? 'currentColor' : 'none'} />
      </button>
      <button
        type="button"
        onClick={async () => {
          await navigator.clipboard.writeText(window.location.href).catch(() => {});
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-gray-900 text-gray-200 border border-gray-700 shadow-lg hover:bg-gray-800"
        aria-label="링크 복사"
        title={copied ? '복사됨' : '링크 복사'}
      >
        <Share2 className="h-5 w-5" />
      </button>
      {copied && (
        <div className="absolute right-14 bottom-1 rounded bg-gold text-black px-2 py-0.5 text-xs font-semibold">
          복사됨!
        </div>
      )}
    </div>
  );
}

// ─── 스켈레톤 ────────────────────────────────────────────

function AnalysisSkeleton() {
  return (
    <div className="space-y-8">
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 items-center">
          <Skeleton className="h-[220px] w-[220px] rounded-full mx-auto" />
          <div className="space-y-3">
            <Skeleton className="h-10 w-72" />
            <Skeleton className="h-7 w-96" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <Skeleton className="h-24 w-full" />
          </Card>
        ))}
      </div>
      <Card>
        <Skeleton className="h-6 w-40" />
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </Card>
      <Card>
        <Skeleton className="h-64 w-full" />
      </Card>
    </div>
  );
}
